import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import type { ArkMove, InvestorNewsItem } from "../api/types";
import Footer from "../components/Footer";
import PageHeader from "../components/PageHeader";
import TopNav from "../components/TopNav";

type Tab = "ark" | "news";
type FundFilter = "all" | "ARKK" | "ARKW" | "ARKG" | "ARKQ" | "ARKF" | "ARKX" | "ARKVX";

const FUND_FILTERS: { id: FundFilter; label: string }[] = [
  { id: "all", label: "All funds" },
  { id: "ARKK", label: "ARKK" },
  { id: "ARKW", label: "ARKW" },
  { id: "ARKG", label: "ARKG" },
  { id: "ARKQ", label: "ARKQ" },
  { id: "ARKF", label: "ARKF" },
  { id: "ARKX", label: "ARKX" },
  { id: "ARKVX", label: "ARKVX" },
];

function useArkMoves() {
  return useQuery<ArkMove[]>({
    queryKey: ["smartmoney", "ark"],
    queryFn: async () => (await api.get<ArkMove[]>("/smartmoney/ark", { params: { days: 7 } })).data,
    staleTime: 5 * 60_000,
  });
}

function useInvestorNews() {
  return useQuery<InvestorNewsItem[]>({
    queryKey: ["smartmoney", "news"],
    queryFn: async () =>
      (await api.get<InvestorNewsItem[]>("/smartmoney/news", { params: { days: 7 } })).data,
    staleTime: 5 * 60_000,
  });
}

function KindBadge({ kind }: { kind: ArkMove["kind"] }) {
  const cls =
    kind === "add" || kind === "new"
      ? "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30"
      : "bg-rose-500/15 text-rose-300 ring-rose-500/30";
  return (
    <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset ${cls}`}>
      {kind}
    </span>
  );
}

function fmtShares(n: number | null): string {
  if (n === null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

export default function SmartMoney() {
  const moves = useArkMoves();
  const news = useInvestorNews();
  const [tab, setTab] = useState<Tab>("ark");
  const [fund, setFund] = useState<FundFilter>("all");

  const filtered = useMemo(() => {
    const all = moves.data ?? [];
    return fund === "all" ? all : all.filter((m) => m.fund === fund);
  }, [moves.data, fund]);
  const newsItems = news.data ?? [];

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <PageHeader />
      <TopNav />

      <div className="mb-3 -mx-1 flex gap-1 overflow-x-auto px-1 pb-1 sm:mb-4">
        {(["ark", "news"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition ${
              tab === t
                ? "bg-slate-800 text-slate-100 ring-1 ring-slate-700"
                : "text-slate-400 hover:bg-slate-800/40 hover:text-slate-200"
            }`}
          >
            {t === "ark" ? "ARK moves" : "Investor mentions"}
          </button>
        ))}
      </div>

      {tab === "news" && (
        <section className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900">
          <ul className="divide-y divide-slate-800">
            {news.isLoading && <li className="px-4 py-10 text-center text-sm text-slate-500">Loading…</li>}
            {!news.isLoading && newsItems.length === 0 && (
              <li className="px-4 py-10 text-center text-sm text-slate-500">
                No investor-tagged news in the last 7 days.
              </li>
            )}
            {newsItems.map((n) => (
              <li key={n.id} className="px-4 py-3">
                <div className="flex items-baseline gap-2">
                  {n.investor_mentions.map((name) => (
                    <span key={name} className="rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-violet-300 ring-1 ring-inset ring-violet-500/30">
                      {name}
                    </span>
                  ))}
                  <span className="text-[11px] text-slate-500">
                    {n.published_at ? new Date(n.published_at).toLocaleDateString() : ""}
                  </span>
                </div>
                <a href={n.url} target="_blank" rel="noreferrer" className="mt-1 block text-sm text-slate-200 hover:text-sky-300">
                  {n.title}
                </a>
                {n.yahoo_symbol && (
                  <Link to={`/s/${encodeURIComponent(n.yahoo_symbol)}`} className="mt-1 inline-block text-[11px] font-mono text-sky-400 hover:text-sky-300">
                    → {n.yahoo_symbol}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {tab === "ark" && (
        <>
      <p className="mb-3 text-xs leading-relaxed text-slate-500 sm:mb-4">
        Daily ARK Invest holdings diffs (last 7 days). Adds and trims relative to the prior trading day.
      </p>

      <div className="mb-3 -mx-1 flex gap-1 overflow-x-auto px-1 pb-1 sm:mb-4">
        {FUND_FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFund(f.id)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition ${
              fund === f.id
                ? "bg-slate-800 text-slate-100 ring-1 ring-slate-700"
                : "text-slate-400 hover:bg-slate-800/40 hover:text-slate-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <section className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900">
        <ul className="divide-y divide-slate-800">
          {moves.isLoading && (
            <li className="px-4 py-10 text-center text-sm text-slate-500">Loading…</li>
          )}
          {!moves.isLoading && filtered.length === 0 && (
            <li className="px-4 py-10 text-center text-sm text-slate-500">
              No ARK moves yet. The fetcher runs daily — first snapshot can take 24h.
            </li>
          )}
          {filtered.map((m, i) => {
            const ticker = m.ticker;
            const detailHref = `/s/${encodeURIComponent(m.yahoo_symbol ?? ticker)}`;
            return (
              <li key={`${m.fund}-${ticker}-${m.snapshot_date}-${i}`}>
                <Link to={detailHref} className="block hover:bg-slate-800/40">
                  <div className="flex items-center gap-3 px-3 py-3 sm:px-4">
                    <div className="w-12 shrink-0 text-right text-[10px] font-medium uppercase tracking-wide text-slate-600">
                      {m.fund}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-base text-slate-100">{ticker}</span>
                        <KindBadge kind={m.kind} />
                      </div>
                      <div className="truncate text-xs text-slate-500">
                        {m.company ?? <span className="italic text-slate-600">{m.snapshot_date}</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm tabular-nums text-slate-200">
                        {fmtShares(m.shares_delta)}
                      </div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-600">
                        {m.weight_pct ? `${m.weight_pct.toFixed(2)}%` : "—"}
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>

      <Footer note="Source: ARK Invest daily holdings CSVs · Refreshes daily post-market US" />
        </>
      )}
    </div>
  );
}
